import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../../services/api';
import Loading from '../../components/Loading';
import { formatStockReason } from '../../utils/formatUtils';
import {
    Table,
    Card,
    Typography,
    Alert,
    Tabs,
    Input,
    InputNumber,
    Button,
    Space,
    Dropdown,
    Menu,
    Tooltip,
    Popover,
    Tag,
    message
} from 'antd';
import { PlusOutlined, CloseOutlined, SettingOutlined, DownloadOutlined, CopyOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import ResponsiveTypography from '../../components/ResponsiveTypography';

const { Title } = ResponsiveTypography;
const { Paragraph, Text } = Typography;
const { TabPane } = Tabs;

const CATEGORY_LEVELS = {
    'level5': 'HS코드',
    'level4': '세세분류',
    'level3': '소분류',
};

const allFilterOptions = [
    { label: '수출액', value: 'expDlr', type: 'range' },
    { label: '수출액 성장률 (%)', value: 'expDlr_growth_%', type: 'range' },
    { label: '수출중량 (t)', value: 'expWgt', type: 'range' },
    { label: '수출중량 성장률 (%)', value: 'expWgt_growth_%', type: 'range' },
    { label: '품목명', value: 'class_name', type: 'match' },
    { label: '상위 분류명', value: 'super_class_name', type: 'match' },
    { label: '분류 코드', value: 'class_code', type: 'match' },
    { label: '상위 분류 코드', value: 'super_class_code', type: 'match' },
];

const getDefaultFilters = () => ({
    'expDlr_growth_%': { min: null, max: null }
});

const ExportSummaryNowPage = () => {
    const [selectedCategory, setSelectedCategory] = useState('level5');
    const [cumulativeMonths, setCumulativeMonths] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [dataDate, setDataDate] = useState('');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stagedFilters, setStagedFilters] = useState(getDefaultFilters());
    const [appliedFilters, setAppliedFilters] = useState(getDefaultFilters());
    const [unit, setUnit] = useState('M');
    const [pageSize, setPageSize] = useState(15);

    useEffect(() => {
        const fetchInfo = async () => {
            if (!selectedCategory) return;
            setLoading(true);
            setError(null);
            setData([]);
            setCumulativeMonths([]);
            setSelectedMonth(null);
            setDataDate('');
            setStagedFilters(getDefaultFilters());
            setAppliedFilters(getDefaultFilters());
            try {
                const response = await apiClient.get(`/current-trade-summary/info/${selectedCategory}`);
                if (response.data && response.data.months) {
                    const { months, latest_date } = response.data;
                    setCumulativeMonths(months);
                    setDataDate(latest_date);
                    if (months.length > 0) {
                        setSelectedMonth(months[0]);
                    } else {
                        setLoading(false);
                    }
                } else {
                    throw new Error('데이터 정보를 불러오는 데 실패했습니다.');
                }
            } catch (err) {
                const errorMessage = err.response?.data?.detail || err.message || '데이터 정보 로딩 중 오류가 발생했습니다.';
                setError(errorMessage);
                setLoading(false);
            }
        };
        fetchInfo();
    }, [selectedCategory]);

    useEffect(() => {
        if (selectedMonth === null || !selectedCategory) return;
        const fetchDataForMonth = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await apiClient.get(`/current-trade-summary/data/${selectedCategory}/${selectedMonth}`);
                setData(response.data);
            } catch (err) {
                const errorMessage = err.response?.data?.detail || err.message || '데이터 로딩 중 오류가 발생했습니다.';
                setError(errorMessage);
            } finally {
                setLoading(false);
            }
        };
        fetchDataForMonth();
    }, [selectedMonth, selectedCategory]);

    const columns = useMemo(() => {
        const baseCols = [
            { title: '순위', key: 'rank', align: 'center', width: 50, render: (text, record, index) => index + 1, fixed: 'left' },
            {
                title: '품목명',
                dataIndex: 'class_name',
                key: 'class_name',
                width: 150,
                ellipsis: true,
                render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>
            },
        ];

        const relatedStocksCol = {
            title: '관련주',
            dataIndex: 'related_stocks',
            key: 'related_stocks',
            width: 200,
            render: (stocks) => {
                if (!stocks || stocks.length === 0) {
                    return <Text type="secondary">-</Text>;
                }
                const validStocks = stocks.filter(stock => stock && stock.company_name && stock.company_name !== '정보 없음');
                if (validStocks.length === 0) {
                    return <Text type="secondary">-</Text>;
                }

                const MAX_VISIBLE_TAGS = 2;

                const allTagsList = (
                    <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '0.25rem', maxWidth: '21.875rem',  }}>
                        {validStocks.map((stock, index) => (
                            <Popover
                                key={index}
                                placement="top"
                                title={<Text strong>{stock.company_name}</Text>}
                                content={
                                    <div style={{ maxWidth: 300 }}>
                                        {formatStockReason(stock.reason)}
                                    </div>
                                }
                                trigger="hover"
                            >
                                <Tag color="blue" style={{ cursor: 'pointer' }}>
                                    {stock.company_name}
                                </Tag>
                            </Popover>
                        ))}
                    </div>
                );

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.125rem' ,  overflowX: 'auto'}}>
                        {validStocks.slice(0, MAX_VISIBLE_TAGS).map((stock, index) => (
                            <Popover
                                key={index}
                                placement="top"
                                title={<Text strong>{stock.company_name}</Text>}
                                content={
                                <div style={{ maxWidth: 300 }}>
                                    {formatStockReason(stock.reason)}
                                </div>

                                }
                                trigger="hover"
                            >
                                <Tag color="blue" style={{ cursor: 'pointer' }}>
                                    {stock.company_name}
                                </Tag>
                            </Popover>
                        ))}

                        {validStocks.length > MAX_VISIBLE_TAGS && (
                            <Popover
                                placement="right"
                                title="전체 관련주"
                                content={allTagsList}
                                trigger="click"
                            >
                                <Tag style={{ cursor: 'pointer', borderStyle: 'dashed' }}>
                                    +{validStocks.length - MAX_VISIBLE_TAGS}
                                </Tag>
                            </Popover>
                        )}
                    </div>
                );
            }
        };

        const valueAndClassificationCols = [
            { title: '수출액', dataIndex: 'expDlr', key: 'expDlr', align: 'right', width: 100, sorter: (a, b) => a.expDlr - b.expDlr,
                render: (value) => {
                    if (unit === 'B') {
                        return <span style={{ fontFamily: 'monospace' }}>{`$${(value / 1000000000).toFixed(2)}B`}</span>;
                    }
                    return <span style={{ fontFamily: 'monospace' }}>{`$${Math.round(value / 1000000).toLocaleString()}M`}</span>;
                },
            },
            { title: '수출액 성장률', dataIndex: 'expDlr_growth_%', key: 'expDlr_growth_%', align: 'right', width: 120, sorter: (a, b) => a['expDlr_growth_%'] - b['expDlr_growth_%'],
                render: (value) => {
                    const numValue = parseFloat(value);
                    const color = numValue > 0 ? '#28a745' : numValue < 0 ? '#dc3545' : 'black';
                    return <span style={{ color, fontWeight: 'bold', fontFamily: 'monospace' }}>{numValue.toFixed(0).padStart(6, ' ')}%</span>;
                },
            },
            { title: '수출중량', dataIndex: 'expWgt', key: 'expWgt', align: 'right', width: 100, sorter: (a, b) => a.expWgt - b.expWgt,
                render: (value) => {
                    const tonnes = Math.round(value / 1000);
                    if (tonnes >= 10000) {
                        const exponent = Math.floor(Math.log10(tonnes));
                        const mantissa = tonnes / Math.pow(10, exponent);
                        return <span style={{ fontFamily: 'monospace' }} dangerouslySetInnerHTML={{__html: `${mantissa.toFixed(1)}&times;10<sup>${exponent}</sup> t`}}></span>;
                    }
                    return <span style={{ fontFamily: 'monospace' }}>{`${tonnes.toLocaleString()} t`}</span>;
                },
            },
            { title: '수출중량 성장률', dataIndex: 'expWgt_growth_%', key: 'expWgt_growth_%', align: 'right', width: 120, sorter: (a, b) => a['expWgt_growth_%'] - b['expWgt_growth_%'],
                render: (value) => {
                    const numValue = parseFloat(value);
                    const color = numValue > 0 ? '#28a745' : numValue < 0 ? '#dc3545' : 'black';
                    return <span style={{ color, fontWeight: 'bold', fontFamily: 'monospace' }}>{numValue.toFixed(0).padStart(6, ' ')}%</span>;
                },
            },
            {
                title: '상위 분류명',
                dataIndex: 'super_class_name',
                key: 'super_class_name',
                width: 120,
                ellipsis: true,
                sorter: (a, b) => String(a.super_class_name || '').localeCompare(String(b.super_class_name || '')),
                render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>
            },
            {
                title: '분류 코드',
                dataIndex: 'class_code',
                key: 'class_code',
                width: 120,
                ellipsis: true,
                sorter: (a, b) => String(a.class_code || '').localeCompare(String(b.class_code || '')),
                render: (text) => {
                    if (text && String(text).length === 10) {
                        return <span style={{ fontFamily: 'monospace' }}>{`${String(text).substring(0, 10)}`}</span>;
                    }
                    return <span style={{ fontFamily: 'monospace' }}>{text}</span>;
                }
            },
        ];

        return [...baseCols, ...(selectedCategory === 'level5' ? [relatedStocksCol] : []), ...valueAndClassificationCols];
    }, [unit, selectedCategory]);

    const handleStagedFilterChange = (key, type, value) => {
        const config = allFilterOptions.find(opt => opt.value === key);
        if (config?.type === 'match') {
            setStagedFilters(prev => ({ ...prev, [key]: { ...prev[key], value: value } }));
        } else {
            const numericValue = value === null || value === '' ? null : value;
            setStagedFilters(prev => ({ ...prev, [key]: { ...prev[key], [type]: numericValue } }));
        }
    };

    const addFilter = (key) => {
        if (!stagedFilters[key]) {
            const config = allFilterOptions.find(opt => opt.value === key);
            setStagedFilters(prev => ({ ...prev, [key]: config?.type === 'match' ? { value: '' } : { min: null, max: null } }));
        }
    };

    const removeFilter = (keyToRemove) => {
        // Prevent removal of the default filter if it's the only one
        if (Object.keys(stagedFilters).length === 1 && stagedFilters[keyToRemove]) {
            message.info('기본 필터는 제거할 수 없습니다. 초기화를 사용해주세요.');
            return;
        }
        const newFilters = { ...stagedFilters };
        delete newFilters[keyToRemove];
        setStagedFilters(newFilters);
    };
    const handleApplyFilters = () => setAppliedFilters(stagedFilters);
    const handleResetFilters = () => {
        setStagedFilters(getDefaultFilters());
        setAppliedFilters(getDefaultFilters());
    };

    const filteredData = useMemo(() => {
        return data.filter(item => {
            return Object.entries(appliedFilters).every(([key, filterValue]) => {
                const config = allFilterOptions.find(opt => opt.value === key);
                const itemValue = item[key];
                if (itemValue === null || itemValue === undefined) return false;

                if (config?.type === 'match') {
                    const { value: searchText } = filterValue;
                    if (!searchText) return true;
                    const searchTerms = searchText.split(',').map(term => term.trim()).filter(term => term);
                    if (searchTerms.length === 0) return true;
                    
                    // Handle dot removal for classification codes
                    return searchTerms.some(term => {
                        let currentItemValue = String(itemValue).toLowerCase();
                        let currentTerm = term.toLowerCase();

                        if (key === 'class_code' || key === 'super_class_code') {
                            currentItemValue = currentItemValue.replace(/\./g, '');
                            currentTerm = currentTerm.replace(/\./g, '');
                        }
                        
                        return currentItemValue.includes(currentTerm);
                    });
                }
                
                // Handle range filters
                const { min, max } = filterValue;
                if (key === 'expDlr') {
                    const scale = unit === 'B' ? 1000000000 : 1000000;
                    if (min !== null && itemValue < (min * scale)) return false;
                    if (max !== null && itemValue > (max * scale)) return false;
                } else if (key === 'expWgt') {
                    const scale = 1000; // From kg to tonnes
                    if (min !== null && itemValue < (min * scale)) return false;
                    if (max !== null && itemValue > (max * scale)) return false;
                } else {
                    if (min !== null && itemValue < min) return false;
                    if (max !== null && itemValue > max) return false;
                }
                return true;
            });
        });
    }, [data, appliedFilters, unit]);
    
    const handleCopyToClipboard = () => {
        if (loading || filteredData.length === 0) {
            message.warn("복사할 데이터가 없습니다.");
            return;
        }
    
        const dataToExport = filteredData.map((item, index) => {
            const row = {
                '순위': index + 1,
                '품목명': item.class_name,
            };
    
            if (selectedCategory === 'level5') {
                const stocks = item.related_stocks;
                let relatedStocksStr = '-';
                if (stocks && stocks.length > 0) {
                    const validStocks = stocks.filter(stock => stock && stock.company_name && stock.company_name !== '정보 없음');
                    if (validStocks.length > 0) {
                        relatedStocksStr = validStocks.map(s => s.company_name).join('; ');
                    }
                }
                row['관련주'] = relatedStocksStr;
            }
    
            row[`수출액 ($${unit})`] = unit === 'B' 
                ? (item.expDlr / 1000000000).toFixed(2)
                : Math.round(item.expDlr / 1000000);
            
            row['수출액 성장률 (%)'] = parseFloat(item['expDlr_growth_%']).toFixed(0);
            row['수출중량 (t)'] = Math.round(item.expWgt / 1000);
            row['수출중량 성장률 (%)'] = parseFloat(item['expWgt_growth_%']).toFixed(0);
            row['상위 분류명'] = item.super_class_name;
            row['분류 코드'] = String(item.class_code);
    
            return row;
        });
    
        const headers = Object.keys(dataToExport[0]);
        const tsvHeader = headers.join('\t');
        
        const tsvRows = dataToExport.map(row => 
            headers.map(header => {
                // Replace newlines and tabs within a cell to avoid breaking TSV format
                return String(row[header] ?? '').replace(/\s/g, ' ');
            }).join('\t')
        );

        const tsvString = [tsvHeader, ...tsvRows].join('\n');
        
        navigator.clipboard.writeText(tsvString).then(() => {
            message.success('표 데이터가 클립보드에 복사되었습니다.');
        }, () => {
            message.error('데이터 복사에 실패했습니다.');
        });
    };

    const handleExportToExcel = () => {
        if (loading || filteredData.length === 0) {
            message.warn("내보낼 데이터가 없습니다.");
            return;
        }

        const dataToExport = filteredData.map((item, index) => {
            const row = { '순위': index + 1, '품목명': item.class_name };
            if (selectedCategory === 'level5') {
                const stocks = item.related_stocks;
                let relatedStocksStr = '-';
                if (stocks && stocks.length > 0) {
                    const validStocks = stocks.filter(stock => stock && stock.company_name && stock.company_name !== '정보 없음');
                    if (validStocks.length > 0) {
                        relatedStocksStr = validStocks.map(s => s.company_name).join(', ');
                    }
                }
                row['관련주'] = relatedStocksStr;
            }
            row[`수출액 ($${unit})`] = unit === 'B' 
                ? parseFloat((item.expDlr / 1000000000).toFixed(2))
                : Math.round(item.expDlr / 1000000);
            row['수출액 성장률 (%)'] = parseFloat(item['expDlr_growth_%']);
            row['수출중량 (t)'] = Math.round(item.expWgt / 1000);
            row['수출중량 성장률 (%)'] = parseFloat(item['expWgt_growth_%']);
            row['상위 분류명'] = item.super_class_name;
            row['분류 코드'] = String(item.class_code);
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "수출입 동향");
        const fileName = `최신_수출입동향_${CATEGORY_LEVELS[selectedCategory]}_${selectedMonth}개월_${dataDate}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    };

    const availableFilterOptions = allFilterOptions.filter(opt => !stagedFilters[opt.value]);
    const filterMenu = (
        <Menu onClick={({ key }) => addFilter(key)}>
            {availableFilterOptions.map(opt => {
                const label = opt.value === 'expDlr' ? `${opt.label} ($${unit})` : opt.label;
                return <Menu.Item key={opt.value}>{label}</Menu.Item>
            })}
        </Menu>
    );

    return (
        <div className="content-box p-4 sm:p-8">

            <Title style={{ marginLeft: "0.625rem", marginRight: "0.625rem", marginBottom: '2.0rem', marginTop: '1.5rem' }} level={2}>품목별 수출입 동향</Title>
            <Paragraph style={{ marginTop: '0.625rem', marginLeft: "0.625rem", marginRight: "0.625rem", fontSize:'1.0rem', wordBreak: 'keep-all' }}>
                    <Text strong style={{fontSize : '1.1rem', color: '#1890ff' }}>{dataDate} </Text> 최신 관세청 수출 데이터를 반영해, 누적 기간별 대한민국 수출 데이터를 제공합니다.
            </Paragraph>
            <Paragraph style={{ marginTop: '0.625rem', marginLeft: "0.625rem", marginRight: "0.625rem", textalign: 'left', wordBreak: 'keep-all'}}>
                <ul>
                    <li>
                        해당 데이터는 공공데이터포털의 관세청 수출 데이터를 기반으로 수집 및 가공되었습니다. 매월 발표되는 수출 데이터는 공공데이터 포털에서 업데이트되는 '다음날' 업데이트 됩니다.
                    </li>
                    <li>
                        관련주 정보는 <Text strong style={{color: '#1890ff' }}>구글 Gemini</Text>를 통해 수집된 것으로, 완전한 정보가 아닙니다. 꼭 다시 검증후 사용하실 것을 부탁드립니다.
                    </li>
                </ul>
            </Paragraph>
            <Paragraph style={{ marginTop: '0.625rem', marginLeft: "0.625rem", marginRight: "0.625rem", textalign: 'left', wordBreak: 'keep-all'}}>
                <Text>품목별 수출액 · 수출중량 데이터를 <Text strong style={{color: '#1890ff' }}>정렬</Text>과 <Text strong style={{color: '#1890ff' }}>필터링</Text> 기능을 활용하여 자신만의 분석을 해보세요! 😊 <br/></Text>
            </Paragraph>

            {error && <Alert message={error} type="error" showIcon style={{ margin: '1.5rem 0' }} />}            

            <Card style={{ marginTop: '1.5rem', marginLeft: '0rem', marginRight: '0rem', padding: '0rem' }} bodyStyle={{ padding: '0.625rem' }}>
                <Tabs style={{marginLeft: '0.625rem'}} activeKey={selectedCategory} onChange={(key) => setSelectedCategory(key)}>
                    {Object.entries(CATEGORY_LEVELS).map(([key, name]) => (<TabPane tab={name} key={key} />))}
                </Tabs>

                {cumulativeMonths.length > 0 && selectedMonth !== null && (
                    <Tabs activeKey={String(selectedMonth)} onChange={(key) => setSelectedMonth(Number(key))} type="card" style={{ marginTop: '1rem' }}>
                        {cumulativeMonths.map(month => (<TabPane tab={`${month}개월 누적`} key={String(month)} />))}
                    </Tabs>
                )}

                <Card size="small" style={{ padding: "0rem", margin: '0.625rem 0rem', background: '#f9fafb' }}>
                    <div style={{ marginTop: '0.3125rem', marginLeft: "0.3125rem", display: 'flex', justifyContent: 'flex-start', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                        <Text strong style={{color: '#1890ff' }} ><SettingOutlined /> 필터 </Text>
                    </div>
                     <div style={{ marginTop: '0.625rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                        <Space wrap>
                            {Object.keys(stagedFilters).map(key => {
                                const config = allFilterOptions.find(opt => opt.value === key);
                                if (!config) return null;
                                if (config.type === 'match') {
                                    return (
                                        <Space key={key} style={{ background: 'white', padding: '0.25rem 0.5rem', borderRadius: '0.375rem', border: '1px solid #d9d9d9' }}>
                                            <Text>{config.label}:</Text>
                                            <Input
                                                size="small" style={{ width: 150 }} placeholder="쉼표(,)로 여러개 입력"
                                                value={stagedFilters[key].value || ''} onChange={(e) => handleStagedFilterChange(key, 'value', e.target.value)}
                                            />
                                            <Button type="text" shape="circle" icon={<CloseOutlined />} size="small" onClick={() => removeFilter(key)} />
                                        </Space>
                                    );
                                }
                                return (
                                    <Space key={key} style={{ background: 'white', padding: '0.25rem 0.5rem', borderRadius: '0.375rem', border: '1px solid #d9d9d9' }}>
                                        <Text>{config.value === 'expDlr' ? `${config.label} ($${unit})` : config.label}:</Text>
                                        <InputNumber size="small" style={{ width: 60 }} placeholder="Min" value={stagedFilters[key].min} onChange={(value) => handleStagedFilterChange(key, 'min', value)} />
                                        <Text>-</Text>
                                        <InputNumber size="small" style={{ width: 60 }} placeholder="Max" value={stagedFilters[key].max} onChange={(value) => handleStagedFilterChange(key, 'max', value)} />
                                        <Button type="text" shape="circle" icon={<CloseOutlined />} size="small" onClick={() => removeFilter(key)} />
                                    </Space>
                                );
                            })}
                            {availableFilterOptions.length > 0 && (
                                <Dropdown overlay={filterMenu} trigger={['click']}>
                                    <Button icon={<PlusOutlined />}>Add filter</Button>
                                </Dropdown>
                            )}
                        </Space>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                        <Space>
                            <Button type="primary" onClick={handleApplyFilters}>적용</Button>
                            <Button onClick={handleResetFilters}>초기화</Button>
                        </Space>
                    </div>
                </Card>
                    <div style={{ marginTop: '1rem', marginLeft: '0.3125rem', marginRight: '0.3125rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Space>
                            <Button icon={<CopyOutlined />} onClick={handleCopyToClipboard} disabled={loading || filteredData.length === 0}>
                                복사
                            </Button>
                            <Button icon={<DownloadOutlined />} onClick={handleExportToExcel} disabled={loading || filteredData.length === 0}>
                                다운로드
                            </Button>
                        </Space>
                    </div>
                    <div style={{ marginTop: '1rem', marginLeft: '0.3125rem', marginRight: '0.3125rem' }}>
                        <Text>모바일 환경에서 표의 전체 열을 보시려면 <Text strong style={{color: '#1890ff'}}>좌·우로 스크롤 </Text>하거나, 가로 방향으로 눕혀서보세요. </Text>
                        <Text><Text strong style={{color: '#1890ff'}}>정렬</Text>기능은 표의 열 제목을 <Text strong style={{color: '#1890ff'}}>클릭</Text>하여 사용할 수 있습니다.<br /></Text>
                    </div>
                    <div style={{ marginTop: '0.625rem', marginLeft: '0.3125rem', marginRight: '0.3125rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space>
                            <Text style={{fontSize: '0.9rem'}} strong> 필터링 결과: <Text style={{fontSize: '1.0rem', color: '#1890ff'}}>{filteredData.length}</Text> 개</Text>
                        </Space>
                        <Space>
                            <Text style={{fontSize: '0.9rem'}} strong> 수출액 단위: </Text>
                            <Button.Group>
                                <Button size='small' type={unit === 'M' ? 'primary' : 'default'} onClick={() => setUnit('M')}>$1M</Button>
                                <Button size='small' type={unit === 'B' ? 'primary' : 'default'} onClick={() => setUnit('B')}>$1B</Button>
                            </Button.Group>
                        </Space>
                    </div>
                {loading ? (
                    <Loading />
                ) : (
                    !error && data.length > 0 && (
                        <Table
                            sticky={{ offsetHeader: 0 }} dataSource={filteredData} columns={columns}
                            rowKey={(record) => `${record.class_code || ''}-${record.class_name}-${record.expDlr}`}
                            pagination={{ 
                                pageSize: pageSize,
                                onShowSizeChange: (current, size) => setPageSize(size),
                                showSizeChanger: true, 
                                pageSizeOptions: ['15', '30', '50', '100', '200'],
                                hideOnSinglePage: true, 
                            }}
                            size="small" scroll={{ x: 1100 }} style={{ marginTop: '0.3125rem' }}
                        />
                    )
                )}
            </Card>
        </div>
    );
};

export default ExportSummaryNowPage;